import React from "react"
import { createPortal } from "react-dom"
import PropTypes from "prop-types"
import debounce from "lodash.debounce"
import { createStructuredSelector } from "reselect"
import { connect } from "react-redux"

// components
import PlaceholderGroup from "app/static/frontend/components/PlaceholderGroup"
import SummernoteHTMLCleanerOptions from "app/static/frontend/components/SummernoteHTMLCleanerOptions"
import { openSummernoteImageLinkDialog } from "app/static/frontend/components/SummernoteInsertImageDialog"
import TemplateLoader from "app/static/frontend/components/TemplateLoader"
import { qual } from "app/static/frontend/QuorumAlert"

// selectors
import {
    getId,
    getPlaceholders,
    hasRequestInProgress,
    htmlContent,
    makeABTestBodyHTMLContent,
} from "shared/outbox/selectors"

import * as templateSelectors from "app/static/frontend/outbox/templates/selectors"

// action creators
import {
    loadTemplate,
    patchBulkEmail,
    resetPreviews,
    updateBulkEmail,
} from "app/static/frontend/outbox/action-creators"

import {
    getAllTemplates,
    loadEmailTemplate,
    patchEmailTemplate,
    updateEmailTemplate,
} from "app/static/frontend/outbox/templates/action-creators"

// helpers
import { makeId } from "app/static/frontend/imports/desktopHelperFunctions" // eslint-disable-line
import { cleanPastedHTML } from "app/static/frontend/imports/cleanPastedHTML"

// css
import "app/static/stylus/reusable_components/summernote.styl"

// it wait time for the auto save function
const DEFAULT_WAIT_TIME = 90000

// Clean Formatting Alert
const AlertText = ({ alertText }) => <div style={{ textAlign: "start" }}>{alertText}</div>

AlertText.propTypes = { alertText: PropTypes.string.isRequired }

export default class SummernoteReact extends React.Component {
    constructor(props) {
        super(props)
        this._randId = makeId()
        this.updateMessageContent = this.updateMessageContent.bind(this)
        this.debouncedUpdateMessageContent = debounce(this.updateMessageContent, 3000)
        this.state = {
            htmlContent: this.props.htmlContent,
            lastSavedHtml: this.props.htmlContent,
            lastUpdate: new Date(),
            clipboardHtml: "",
        }
    }

    /**
     * Once the component mounts, we initialize summernote. This means making t
     *
     */
    componentDidMount() {
        /**
         * This function creates a swal popup that will strip bad html out of the summernote editor
         * @param {String} alertText the text to display in the swal
         */
        const alertCleanHtml = async (alertText) => {
            let validStyleDeclarationKeys

            const { value } = await qual.fire({
                icon: "paste",
                iconFamily: "fas",
                title: "Formatting Error",
                text: <AlertText alertText={alertText} />,
                actions: {
                    cancel: "Retain Original",
                    confirm: "Clean Text",
                },
                children: (
                    <SummernoteHTMLCleanerOptions
                        onChange={(values) => {
                            validStyleDeclarationKeys = values
                        }}
                    />
                ),
            })

            validStyleDeclarationKeys = validStyleDeclarationKeys || Boolean(value)

            if (validStyleDeclarationKeys) {
                $(this._summernoteElt).summernote(
                    "code",
                    cleanPastedHTML({
                        input: $(this._summernoteElt).summernote("code"),
                        validStyleDeclarationKeys,
                    }),
                )
            }

            this.setState({ clipboardHtml: "" })
        }

        /**
         * This is a template button - it can optionally take in
         *   the context of summernote, but we just want to create it
         * @param {Object} context context of summernote
         * @returns {Object} a jQuery Object
         */
        const placeholderId = `placeholder-rt-${this._randId}`
        function PlaceholderButton() {
            return $(`<div id="${placeholderId}" class="note-btn-group btn-group"></div>`)
        }
        this.setState({ placeholderId })

        /**
         * This is a template button - it can optionally take in
         *   the context of summernote, but we just want to create it
         * @param {Object} context context of summernote
         * @returns {Object} a jQuery Object
         */
        const templateId = `template-rt-${this._randId}`
        function TemplateButton() {
            return $(`<div id="${templateId}" class="note-btn-group btn-group"></div>`)
        }
        this.setState({ templateId })

        /**
         * Link Image button replaces the picture button built into Summernote. We reimplement this functionality so
         * we can allow clients to create a link from the image dialog.
         */
        const LinkImageButton = (context) => {
            return $.summernote.ui
                .button({
                    contents: '<i class="note-icon-picture" />',
                    tooltip: "Picture",
                    click: async () => {
                        const dialogResult = await openSummernoteImageLinkDialog()
                        if (!dialogResult) {
                            return
                        }

                        const image = document.createElement("img")

                        if (this.props.fullWidthImage) {
                            image.setAttribute("width", "100%")
                        } else {
                            // Match the behavior of summernote's picture dialog
                            const editableWidth = context.layoutInfo.editable.width()
                            const onLoadHandler = (_evt) => {
                                image.setAttribute("width", `${Math.min(image.width, editableWidth)}px`)
                                image.removeEventListener("load", onLoadHandler)
                            }
                            image.addEventListener("load", onLoadHandler)
                        }

                        image.setAttribute("src", dialogResult.imageUrl)
                        image.setAttribute("data-filename", dialogResult.imageUrl)

                        let parent
                        if (dialogResult.imageLink) {
                            parent = document.createElement("a")
                            parent.href = dialogResult.imageLink
                            parent.appendChild(image)
                        } else {
                            parent = image
                        }

                        return $(this._summernoteElt).summernote("insertNode", parent)
                    },
                })
                .render()
        }

        /**
         * This is a custom button - it can optionally take in
         *   the context of summernote, but we just want to create it
         * @param {Object} context context of summernote
         * @returns {Object} a jQuery Object
         */
        // https://summernote.org/deep-dive/#custom-button
        const CleanerButton = () => {
            const { ui } = $.summernote

            // create button
            const button = ui.button({
                contents: "Clean Formatting",
                tooltip: "Clean External Formatting",
                click: () => {
                    const alertText =
                        "This will clean formatting added by Word, Outlook, or another website. While your text may look " +
                        "okay in Quorum, different web browsers and email clients may render it poorly.\n\nTo fix this problem, " +
                        'you should either type your text directly into Quorum or click the "Clean Text" ' +
                        "button below. \n\nWarning: Clicking 'Clean Text' will clean the entire text, " +
                        "but will attempt to retain basic markup"

                    alertCleanHtml(alertText)
                },
            })

            return button.render() // return button as jquery object
        }

        $(this._summernoteElt).summernote({
            dialogsInBody: true,
            height: this.props.height,
            codemirror: {
                theme: "monokai",
                htmlMode: true,
                mode: "text/html",
                lineNumbers: true,
            },
            codeviewIframeFilter: false,
            focus: this.props.isFocused,
            placeholder: this.props.textboxPlaceholder,
            fontNames: [
                "Helvetica Neue",
                "Arial",
                "Arial Black",
                "Comic Sans MS",
                "Courier New",
                "Helvetica",
                "Impact",
                "Lucida Grande",
                "Tahoma",
                "Times New Roman",
                "Verdana",
                "Calibri",
                "Montserrat",
            ],
            buttons: {
                templates: TemplateButton,
                placeholders: PlaceholderButton,
                cleaner: CleanerButton,
                linkimage: LinkImageButton,
            },
            toolbar: [
                ["font", this.props.fontNames],
                ["font", ["fontsize"]],
                ["style", this.props.fontStyles],
                ["cleaners", ["clear"]],
                ["para", ["style", "paragraph", "ul", "ol", "height"]],
                ["insert", this.props.insertTools],
                ["view", this.props.hideFullscreen ? ["codeview"] : ["fullscreen", "codeview"]],
                ["mybutton", ["cleaner"]],
                ["custom", ["templates", "placeholders"]],
            ],
            // https://github.com/summernote/summernote/pull/2554
            maxTextLength: this.props.maxTextLength,
            callbacks: {
                onInit: () => {
                    this.setState({ initialized: true })
                },
                onFocus: () => {
                    $(".dropdown-toggle.show").click()
                },
                onBlur: () => {
                    if (this.props.needsAutosave) {
                        // when we autosave on blur, we want to batch calls to
                        // update since it sends a patch request and we can avoid
                        // patching when there are many onBlur events in a row
                        this.debouncedUpdateMessageContent(0, true)
                    } else {
                        this.props.onBlur()
                    }
                },
                onChange: (contents) => {
                    // only autosave messages if the summernote needs it
                    if (this.props.needsAutosave) {
                        this.setState({ htmlContent: contents }, () => this.updateMessageContent(0))
                    } else {
                        this.setState({ htmlContent: contents }, this.props.editTextObject({ html_content: contents }))
                    }
                },
                onPaste: (e) => {
                    // https://stackoverflow.com/a/31019586
                    // https://stackoverflow.com/a/30904519
                    const event = e.originalEvent || window.event || e
                    const clipboardData = event.clipboardData || window.clipboardData

                    // https://www.w3.org/TR/clipboard-apis/#override-paste
                    if (clipboardData && clipboardData.getData) {
                        const htmlData = clipboardData.getData("text/html")

                        if (htmlData) {
                            this.setState({ clipboardHtml: htmlData })
                        }
                    }
                },
                onImageUpload: (files) => {
                    const data = new FormData()
                    data.append("file", files[0])
                    return $.ajax({
                        data,
                        type: "POST",
                        url: "/outbox/upload_image/",
                        cache: false,
                        contentType: false,
                        processData: false,
                        success: (url) => {
                            const addFullWidthImage = (image) => {
                                image.attr("data-filename", url)
                                image.css("width", "100%")
                            }
                            return $(this._summernoteElt).summernote(
                                "insertImage",
                                url,
                                // The third argument for the 'insertImage' function accepts either a function or a string
                                // If it is a function, runs the function with the img element as a parameter
                                // If it is a string, adds the url as attribute 'data-filename'
                                this.props.fullWidthImage ? addFullWidthImage : url,
                            )
                        },
                    })
                },
            },
            onCreateLink: (link) => {
                // from 0.8.2, summernote automatically adds http:// to links that does not have http
                // https://github.com/summernote/summernote/issues/1037#issuecomment-277750213
                // if the link is a placeholder, we want to remove the http://
                if (link.startsWith("http://") && /\{\{[A-Za-z0-9_]*\}\}/.test(link)) {
                    return link.slice(7)
                }
                return link
            },
            hint: {
                match: /\{\{(\w*)$/,
                options: () => this.props.placeholders.reduce((all, category) => all.concat(category.items), []),
                // search function
                search(keyword, callback) {
                    return callback(
                        this.options()
                            .filter(
                                (placeholder) =>
                                    placeholder.value.includes(keyword) || placeholder.name.includes(keyword),
                            )
                            .slice(0, 5),
                    )
                },
                // format the placeholders
                template(item) {
                    return `<span><b>${item.name}</b> (<i>${item.slug}</i>)</span>`
                },
                // how the placeholder is inserted
                content: (placeholder) => {
                    // Workaround for a summernote bug introduced in 0.8.15 where the textarea is not updated after hints are inserted.
                    // To ensure the just inserted placeholder is persisted, we call setState after the placeholder is inserted.
                    // See: https://github.com/summernote/summernote/issues/3660
                    setTimeout(() => {
                        this.setState({ htmlContent: $(this._summernoteElt).summernote("code") }, () =>
                            this.updateMessageContent(0),
                        )
                    }, 100)
                    return placeholder.value
                },
            },
        })

        $(".summernote-element-div .dropdown-toggle").on("blur", (e) => {
            // if it is a click outside of the dropdown, close the dropdown
            if (!e.relatedTarget) {
                $(e.target).click()
            }
        })

        // replace the icky summernote formatting
        $(this._summernoteContainer).find(".btn-default").removeClass("btn-default").addClass("btn-default-white")
        // update the code
        $(this._summernoteElt).summernote("code", this.props.htmlContent)

        // https://github.com/DiemenDesign/summernote-cleaner/blob/master/summernote-cleaner.js
        // https://gist.github.com/jasonbyrne/ed3572949091347bca90
        // https://gist.github.com/hiteshaggarwal/388cd3fae7331aa0415c63e0a883e8f5
        // required since Summernote has not onInput callback
        $(this._summernoteElt)
            .next()
            .find(".note-editable")
            .on("input", (e) => {
                if (e.originalEvent.inputType === "insertFromPaste" && this.state.clipboardHtml) {
                    const alertText =
                        "You just pasted something into Quorum that may contain formatting from " +
                        "another program like Word, Outlook, or another website. While your text may look " +
                        "okay in Quorum, different web browsers and email clients may render it poorly.\n\nTo fix this problem, " +
                        'you should either type your text directly into Quorum or click the "Clean Text" ' +
                        "button below. \n\nWarning: Clicking 'Clean Text' will clean the entire text, " +
                        "but will attempt to retain basic markup"

                    alertCleanHtml(alertText)
                }
            })

        this.fixBootstrapDataElements("data-toggle")
        this.fixBootstrapDataElements("data-dismiss")

        // this is to fix the font sizes from not applying past the first line break
        // https://github.com/summernote/summernote/issues/3963#issuecomment-876953821
        $.summernote.dom.walkPoint = (function (_super) {
            return function () {
                var startPoint = arguments[0]
                var endPoint = arguments[1]
                var handler = arguments[2]
                var isSkipInnerOffset = arguments[3]
                let point = startPoint

                while (point) {
                    handler(point)

                    if ($.summernote.dom.isSamePoint(point, endPoint)) {
                        break
                    }
                    const isSkipOffset =
                        isSkipInnerOffset && startPoint.node !== point.node && endPoint.node !== point.node
                    point = $.summernote.dom.nextPoint(point, isSkipOffset)
                }
            }
        })($.summernote.dom.walkPoint)
    }

    /**
     * Update the summernote html if it doesn't equal the current code
     *
     * @param {Object} nextProps new props object
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
        // this is hacky an gross, but this is how we can send next props to the
        // placeholders
        if (this.props.hasPlaceholders && nextProps.placeholders !== this.props.placeholders) {
            this._placeholderGroup.updatePlaceholders(nextProps.placeholders)
        }
        if (this.props.hasTemplates && nextProps.templates !== this.props.templates) {
            this._templateTool.updateTemplates(nextProps.templates)
        }
        if (!this.props.needsAutosave) {
            this.setState({ lastSavedHtml: nextProps.htmlContent })
        }
        // if there is a next id, update the html code
        if (nextProps.id !== this.props.id) {
            $(this._summernoteElt).summernote("code", nextProps.htmlContent)
        } else if (
            nextProps.htmlContent !== this.state.htmlContent &&
            nextProps.htmlContent !== this.state.lastSavedHtml
        ) {
            $(this._summernoteElt).summernote("code", nextProps.htmlContent)
        }
    }

    /**
     * Whether the component should rerender
     *
     * @param {Object} nextProps the next props object
     * @param {Object} nextState the next state object
     * @returns {Boolean} whether it should rerender
     */
    shouldComponentUpdate(nextProps, nextState) {
        const shouldRender =
            (nextState.placeholderId && nextState.placeholderId !== this.state.placeholderId) ||
            (nextState.templateId && nextState.templateId !== this.state.templateId)
        return shouldRender
    }

    componentWillUnmount() {
        if (this.state.initialized) {
            $(this._summernoteElt).summernote("destroy")
        } else {
            console.warn("Summernote was not initialized on this element.")
        }
    }

    /**
     * Method called on change. If the message isn't being saved, and
     *  there has been a change we reset the
     *  previews and we auto save the message
     *
     *  @param {Number} waitTime - the amount of time to wait (90sec default)
     *  @param {Boolean} forcePatch - force updating the store and patching the content
     *
     */
    updateMessageContent(waitTime = DEFAULT_WAIT_TIME, forcePatch = false) {
        // if the html content has changed - update the content
        if (!this.props.needsAutosave) {
            this.setState(
                { htmlContent: this.state.htmlContent },
                this.props.editTextObject({ html_content: this.state.htmlContent }),
            )
            return
        }

        if (forcePatch || this.state.htmlContent !== this.state.lastSavedHtml) {
            const now = new Date()
            const timeDiff = now - this.state.lastUpdate

            // auto update the message if there was a change and it currently isn't updating
            if (forcePatch || waitTime === 0 || (!this.props.isUpdating && timeDiff > waitTime)) {
                // if we have content, and the last update was in the last 2 seconds, then
                // we update the last saved html and we patch
                this.setState({
                    lastSavedHtml: this.state.htmlContent,
                    lastUpdate: now,
                })
                this.props.editTextObject({ html_content: this.state.htmlContent })
                // if the html content changed - the placeholder status should
                // be reverted to ready!
                this.props.resetPreviews(this.props.variantId)
                // we don't want to kick off any patch requests unless
                // we have actually had enough time - this 8 second buffer is just a safety net
                if (forcePatch || timeDiff > 8000) {
                    this.props.patchTextObject()
                }
            }
        }
    }

    fixBootstrapDataElements(dataString) {
        // data-toggle elements need to have data-bs-toggle elements for bootstrap 5
        // this is a hacky solution to add the attribute to the dropdown elements
        const $elements = $(this._summernoteElt).next().find(`[${dataString}]`)
        const newDataAttribute = dataString.split("-").join("-bs-")

        for (const element of $elements) {
            const value = $(element).attr(dataString)
            $(element).attr(newDataAttribute, value)
            $(element).removeAttr(dataString)
        }
    }

    render() {
        const renderPlaceholdersGroup = this.props.hasPlaceholders && this.state.placeholderId
        const renderTemplateLoader = this.props.hasTemplates && this.state.templateId
        return (
            <>
                <div
                    id={`summernote-container-${this._randId}`}
                    ref={(elt) => (this._summernoteContainer = elt)}
                    className={`summernote-element-div object-id-${this.props.id}`}
                    data-cy={this.props.dataCy}
                >
                    <div
                        id={`summernote-editor-${this._randId}`}
                        ref={(elt) => (this._summernoteElt = elt)}
                        className={`summernote-${this.props.summernoteName}`}
                    />
                </div>
                {renderPlaceholdersGroup &&
                    createPortal(
                        <PlaceholderGroup
                            ref={(elt) => (this._placeholderGroup = elt)}
                            summernoteElt={this._summernoteElt}
                            placeholders={this.props.placeholders}
                        />,
                        document.getElementById(this.state.placeholderId),
                    )}
                {renderTemplateLoader &&
                    createPortal(
                        <TemplateLoader
                            initialTemplates={this.props.templates}
                            loadTemplate={this.props.loadTemplate}
                            getTemplates={this.props.getAllTemplates}
                            ref={(elt) => (this._templateTool = elt)}
                            small
                            variantId={this.props.variantId}
                        />,
                        document.getElementById(this.state.templateId),
                    )}
            </>
        )
    }
}
SummernoteReact.propTypes = {
    dataCy: PropTypes.string,
    editTextObject: PropTypes.func.isRequired,
    fontNames: PropTypes.arrayOf(PropTypes.string),
    fontStyles: PropTypes.arrayOf(PropTypes.string),
    fullWidthImage: PropTypes.bool,
    getAllTemplates: PropTypes.func,
    hasPlaceholders: PropTypes.bool.isRequired,
    hasTemplates: PropTypes.bool.isRequired,
    height: PropTypes.string,
    htmlContent: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    insertTools: PropTypes.arrayOf(PropTypes.string),
    isFocused: PropTypes.bool,
    isUpdating: PropTypes.bool.isRequired,
    loadTemplate: PropTypes.func,
    maxTextLength: PropTypes.number,
    needsAutosave: PropTypes.bool,
    onBlur: PropTypes.func,
    patchTextObject: PropTypes.func,
    placeholders: PropTypes.arrayOf(PropTypes.object),
    resetPreviews: PropTypes.func,
    summernoteName: PropTypes.string.isRequired,
    templates: PropTypes.arrayOf(PropTypes.object),
    textboxPlaceholder: PropTypes.string,
    variantId: PropTypes.number,
}
SummernoteReact.defaultProps = {
    dataCy: "",
    fontNames: ["fontname"],
    fontStyles: ["bold", "italic", "underline", "color"],
    fullWidthImage: false,
    getAllTemplates() {
        return
    },
    height: "400px",
    htmlContent: "",
    id: 0,
    insertTools: ["linkimage", "link", "table", "hr"],
    loadTemplate() {
        return
    },
    needsAutosave: true,
    onBlur() {
        return
    },
    patchTextObject() {
        return
    },
    placeholders: [],
    resetPreviews() {
        return
    },
    templates: [],
    textboxPlaceholder: "",
}

// for Outbox
const mapBulkEmailStateToProps = createStructuredSelector({
    htmlContent,
    id: getId,
    isUpdating: hasRequestInProgress,
    placeholders: getPlaceholders,
    templates: templateSelectors.getSummernoteTemplates,
})

const mapBulkEmailDispatchToProps = {
    editTextObject: updateBulkEmail,
    getAllTemplates,
    loadTemplate,
    patchTextObject: patchBulkEmail,
    resetPreviews,
}

export const BulkEmailSummernote = connect(mapBulkEmailStateToProps, mapBulkEmailDispatchToProps)(SummernoteReact)

// connection for Outbox AB test body content variants
const mapBulkEmailStateToPropsABTestingBody = createStructuredSelector({
    htmlContent: (state, props) => makeABTestBodyHTMLContent(props.variantId)(state),
    isUpdating: hasRequestInProgress,
    placeholders: getPlaceholders,
    templates: templateSelectors.getSummernoteTemplates,
})

const mapBulkEmailDispatchToPropsABTestingBody = {
    getAllTemplates,
    loadTemplate,
    patchTextObject: patchBulkEmail,
    resetPreviews,
}

export const ConnectedBulkEmailSummernoteABTestingBody = connect(
    mapBulkEmailStateToPropsABTestingBody,
    mapBulkEmailDispatchToPropsABTestingBody,
)(SummernoteReact)

// for templates
const mapTemplateStateToProps = createStructuredSelector({
    htmlContent: templateSelectors.getHtmlContent,
    id: templateSelectors.getId,
    isUpdating: templateSelectors.isUpdating,
    placeholders: templateSelectors.getPlaceholders,
    templates: templateSelectors.getSummernoteTemplates,
})
const mapTemplateDispatchToProps = {
    editTextObject: updateEmailTemplate,
    getAllTemplates,
    loadTemplate: loadEmailTemplate,
    patchTextObject: patchEmailTemplate,
}

export const TemplateSummernote = connect(mapTemplateStateToProps, mapTemplateDispatchToProps)(SummernoteReact)
